import { useState } from 'react';
import type { NewTopicInput } from '../../types/whatsapp';

const fieldClass =
  'w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm leading-6 text-stone-800 outline-none transition-all focus:border-primary focus:ring-4 focus:ring-primary/10';
const labelClass = 'text-[13px] font-semibold text-stone-800';
const hintClass = 'text-xs leading-[1.45] text-stone-600';

export const LABEL_MAX = 120;

export const emptyTopic: NewTopicInput = { label: '', description: '', examples: '', content: '' };

/** The reason a topic can't be saved yet, or null. Mirrors what the API requires. */
export const topicError = (v: NewTopicInput, fullForm: boolean): string | null => {
  if (fullForm) {
    if (!v.label.trim()) return 'Escribe el nombre del tema.';
    if (v.label.trim().length > LABEL_MAX) return `El nombre admite hasta ${LABEL_MAX} caracteres.`;
    if (!v.description.trim()) return 'Describe cuándo aplica el tema.';
  }
  if (!v.content.trim()) return 'Escribe el contenido que el asistente puede usar.';
  return null;
};

interface TopicFormProps {
  /** A custom topic (name, when it applies, examples and content) or only the content of a predefined one. */
  fullForm: boolean;
  initial: NewTopicInput;
  idPrefix: string;
  saveLabel: string;
  saving: boolean;
  error: string | null;
  footerNote?: string;
  onSave: (values: NewTopicInput) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

export const TopicForm = ({
  fullForm,
  initial,
  idPrefix,
  saveLabel,
  saving,
  error,
  footerNote,
  onSave,
  onCancel,
  onDelete,
}: TopicFormProps) => {
  const [values, setValues] = useState<NewTopicInput>(initial);
  const [touched, setTouched] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const dirty = (Object.keys(initial) as (keyof NewTopicInput)[]).some((k) => values[k] !== initial[k]);
  const validation = touched ? topicError(values, fullForm) : null;
  const set = (k: keyof NewTopicInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (topicError(values, fullForm)) return;
    onSave({
      label: values.label.trim(),
      description: values.description.trim(),
      examples: values.examples.trim(),
      content: values.content.trim(),
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {fullForm && (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${idPrefix}-nombre`} className={labelClass}>Nombre del tema</label>
            <input
              id={`${idPrefix}-nombre`}
              type="text"
              value={values.label}
              onChange={set('label')}
              maxLength={LABEL_MAX}
              className={`${fieldClass} h-11 py-0`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${idPrefix}-cuando`} className={labelClass}>¿Cuándo aplica?</label>
            <textarea id={`${idPrefix}-cuando`} rows={2} value={values.description} onChange={set('description')} className={`${fieldClass} resize-y`} />
            <span className={hintClass}>
              Describe en una o dos frases qué mensajes pertenecen a este tema. El asistente lo usa para reconocerlo, así que sé específico.
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${idPrefix}-ejemplos`} className={labelClass}>
              Ejemplos de mensajes <span className="font-normal text-stone-600">(opcional)</span>
            </label>
            <textarea id={`${idPrefix}-ejemplos`} rows={3} value={values.examples} onChange={set('examples')} className={`${fieldClass} resize-y`} />
            <span className={hintClass}>
              Uno por línea. Ayudan a que el asistente reconozca el tema aunque el paciente lo pregunte de otra forma.
            </span>
          </div>
        </>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-contenido`} className={labelClass}>Contenido que el asistente puede usar</label>
        <textarea id={`${idPrefix}-contenido`} rows={fullForm ? 4 : 5} value={values.content} onChange={set('content')} className={`${fieldClass} resize-y`} />
        <span className={hintClass}>
          El asistente solo responde con lo que escribas aquí. Si la pregunta no está cubierta, pasa al equipo.
        </span>
      </div>

      {(validation || error) && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {validation ?? error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-stone-200 pt-3">
        <div className="flex items-center gap-4">
          {footerNote && <span className="text-[13px] text-stone-600">{footerNote}</span>}
          {!footerNote && dirty && (
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-amber-800">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Cambios sin guardar
            </span>
          )}
          {onDelete && !confirmDelete && (
            <button type="button" onClick={() => setConfirmDelete(true)} className="border-0 bg-transparent p-0 text-[13px] font-semibold text-red-700">
              Eliminar tema
            </button>
          )}
          {onDelete && confirmDelete && (
            <span className="flex items-center gap-2 text-[13px] text-stone-700">
              ¿Eliminar este tema?
              <button type="button" onClick={onDelete} disabled={saving} className="border-0 bg-transparent p-0 font-semibold text-red-700 disabled:opacity-50">
                Sí, eliminar
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} className="border-0 bg-transparent p-0 font-semibold text-stone-600">
                No
              </button>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="h-10 rounded-full border border-stone-200 bg-white px-[18px] text-sm font-semibold text-stone-600 hover:bg-stone-50">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="h-10 rounded-full border-0 bg-primary px-5 text-sm font-semibold text-white disabled:opacity-60">
            {saving ? 'Guardando…' : saveLabel}
          </button>
        </div>
      </div>
    </form>
  );
};
